import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useSafeBack } from '../hooks/useSafeBack';
import assessmentService from '../services/assessmentService';
import { studentService } from '../services/studentService';
import { toast } from 'react-toastify';

const ResultsPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [shareDialog, setShareDialog] = useState(false);
  const [appointmentDialog, setAppointmentDialog] = useState(false);
  const [selectedResult, setSelectedResult] = useState<any>(null);
  const [shareMessage, setShareMessage] = useState('');
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadResults();
  }, []);

  const loadResults = async () => {
    try {
      setLoading(true);
      const response = await studentService.getMyResults();
      
      // Handle paginated response
      const data = (response as any).content || response;
      
      // Transform the data to match the expected format
      const transformedResults = (Array.isArray(data) ? data : []).map((result: any) => ({
        id: result.id,
        assessmentId: result.surveyId,
        title: result.surveyTitle || 'Assessment',
        completedDate: result.createdAt,
        score: result.totalScore || 0,
        maxScore: 100, // This might need to come from somewhere else
        riskLevel: result.riskLevel === 'RED' ? 'High' : result.riskLevel === 'YELLOW' ? 'Moderate' : 'Low',
        interpretation: getRiskInterpretation(result.riskLevel),
        recommendations: getRiskRecommendations(result.riskLevel),
        immediateFlag: result.immediateFlag
      }));
      
      setResults(transformedResults);
    } catch (error) {
      console.error('Error loading results:', error);
      toast.error('Failed to load assessment results');
    } finally {
      setLoading(false);
    }
  };

  const getRiskInterpretation = (riskLevel: string): string => {
    switch (riskLevel) {
      case 'RED':
        return 'High risk levels detected. Immediate attention recommended.';
      case 'YELLOW':
        return 'Some indicators of concern were detected. Consider speaking with a counselor.';
      case 'GREEN':
      default:
        return 'Your levels appear to be within normal range. Continue practicing healthy coping strategies.';
    }
  };

  const getRiskRecommendations = (riskLevel: string): string[] => {
    switch (riskLevel) {
      case 'RED':
        return [
          'Urgent: Schedule counselor appointment',
          'Practice stress reduction techniques',
          'Review workload and priorities',
          'Consider time management strategies'
        ];
      case 'YELLOW':
        return [
          'Schedule appointment with counselor',
          'Engage in social activities',
          'Consider mood tracking',
          'Maintain healthy lifestyle habits'
        ];
      case 'GREEN':
      default:
        return [
          'Maintain regular exercise routine',
          'Practice mindfulness meditation',
          'Ensure adequate sleep (7-9 hours)'
        ];
    }
  };

  const handleLogout = () => {
    logout();
  };

  const goBack = useSafeBack();

  const handleDownloadReport = async (result: any) => {
    try {
      // Use the PDF service to download the assessment report
      if (user?.userId && result.assessmentId) {
        await assessmentService.downloadAssessmentPdf(user.userId, result.assessmentId);
        toast.success('PDF report downloaded successfully!');
      } else {
        // Fallback to comprehensive report if no specific assessment ID
        await assessmentService.downloadComprehensivePdf(user?.userId || 0);
        toast.success('Comprehensive PDF report downloaded successfully!');
      }
    } catch (error) {
      console.error('Error downloading PDF report:', error);
      toast.error('Failed to download PDF report');
    }
  };

  const handleShareWithCounselor = (result: any) => {
    setSelectedResult(result);
    setShareMessage(`Hi, I would like to share my ${result.title} assessment results with you. My score was ${result.score}/${result.maxScore} with a ${result.riskLevel} risk level. Please review and let me know if we should discuss this further.`);
    setShareDialog(true);
  };

  const handleScheduleAppointment = (result: any) => {
    setSelectedResult(result);
    setAppointmentDialog(true);
  };

  const handleShareSubmit = () => {
    // Share with counselor via API
    
    setShareDialog(false);
    setShareMessage('');
    setSelectedResult(null);
    setSnackbarMessage('Results shared with counselor successfully!');
    setShowSnackbar(true);
  };

  const handleAppointmentSubmit = () => {
    // Simulate scheduling appointment
    const appointmentData = {
      studentId: user?.userId,
      reason: `Follow-up for ${selectedResult?.title} assessment`,
      riskLevel: selectedResult?.riskLevel,
      requestedDate: new Date().toISOString(),
      notes: `Score: ${selectedResult?.score}/${selectedResult?.maxScore}, Risk: ${selectedResult?.riskLevel}`
    };
    
    // Submit appointment request via API
    
    setAppointmentDialog(false);
    setSelectedResult(null);
    setSnackbarMessage('Appointment request submitted! You will be contacted within 24 hours.');
    setShowSnackbar(true);
  };

  const mockResults = [
    {
      id: 1,
      assessmentId: 1,
      title: 'Anxiety Questionnaire',
      completedDate: '2025-06-25',
      score: 12,
      maxScore: 20,
      riskLevel: 'Low',
      interpretation: 'Your anxiety levels appear to be within normal range. Continue practicing healthy coping strategies.',
      recommendations: [
        'Maintain regular exercise routine',
        'Practice mindfulness meditation',
        'Ensure adequate sleep (7-9 hours)'
      ]
    },
    {
      id: 2,
      assessmentId: 2,
      title: 'Depression Screening',
      completedDate: '2025-06-20',
      score: 8,
      maxScore: 15,
      riskLevel: 'Moderate',
      interpretation: 'Some indicators of depression were detected. Consider speaking with a counselor.',
      recommendations: [
        'Schedule appointment with counselor',
        'Engage in social activities',
        'Consider mood tracking',
        'Maintain healthy lifestyle habits'
      ]
    },
    {
      id: 3,
      assessmentId: 3,
      title: 'Stress Assessment',
      completedDate: '2025-06-15',
      score: 16,
      maxScore: 20,
      riskLevel: 'High',
      interpretation: 'High stress levels detected. Immediate attention recommended.',
      recommendations: [
        'Urgent: Schedule counselor appointment',
        'Practice stress reduction techniques',
        'Review workload and priorities',
        'Consider time management strategies'
      ]
    }
  ];

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'Low': return 'success';
      case 'Moderate': return 'warning';
      case 'High': return 'error';
      default: return 'default';
    }
  };

  const getProgressColor = (score: number, maxScore: number) => {
    const percentage = (score / maxScore) * 100;
    if (percentage <= 40) return 'success';
    if (percentage <= 70) return 'warning';
    return 'error';
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Psychology Profile System - My Results
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Button variant="outlined" onClick={goBack}>
            ← Back to Dashboard
          </Button>
          <Typography variant="h4">
            My Assessment Results
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {loading ? (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          </Grid>
        ) : results.length === 0 ? (
          <Grid item xs={12}>
            <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
              No assessment results found. Complete an assessment to see your results here.
            </Typography>
          </Grid>
        ) : (
          results.map((result) => (
            <Grid item xs={12} key={result.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6">
                      {result.title}
                    </Typography>
                    <Chip 
                      label={`${result.riskLevel} Risk`} 
                      color={getRiskColor(result.riskLevel) as any}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Completed on: {result.completedDate}
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">
                        Score: {result.score} / {result.maxScore}
                      </Typography>
                      <Typography variant="body2">
                        {Math.round((result.score / result.maxScore) * 100)}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(result.score / result.maxScore) * 100}
                      color={getProgressColor(result.score, result.maxScore) as any}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  
                  <Typography variant="body1" paragraph>
                    <strong>Interpretation:</strong> {result.interpretation}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Recommendations:</strong>
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                    {result.recommendations.map((rec: string, index: number) => (
                      <Typography component="li" variant="body2" key={index} sx={{ mb: 0.5 }}>
                        {rec}
                      </Typography>
                    ))}
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="outlined" size="small" onClick={() => handleDownloadReport(result)}>
                      Download Report
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => handleShareWithCounselor(result)}>
                      Share with Counselor
                    </Button>
                    {result.riskLevel !== 'Low' && (
                      <Button variant="contained" size="small" color="primary" onClick={() => handleScheduleAppointment(result)}>
                        Schedule Appointment
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
        </Grid>
      </Container>

      {/* Share with Counselor Dialog */}
      <Dialog open={shareDialog} onClose={() => setShareDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Share Assessment Results - {selectedResult?.title}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              You are about to share your assessment results with your assigned counselor.
            </Typography>
            
            <Box sx={{ my: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Assessment Summary:
              </Typography>
              <Typography variant="body2">
                <strong>Assessment:</strong> {selectedResult?.title}<br/>
                <strong>Score:</strong> {selectedResult?.score} / {selectedResult?.maxScore}<br/>
                <strong>Risk Level:</strong> {selectedResult?.riskLevel}<br/>
                <strong>Date:</strong> {selectedResult?.completedDate}
              </Typography>
            </Box>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Message to Counselor (optional)"
              value={shareMessage}
              onChange={(e) => setShareMessage(e.target.value)}
              placeholder="Add any additional notes or questions for your counselor..."
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialog(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleShareSubmit}>
            Share Results
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Appointment Dialog */}
      <Dialog open={appointmentDialog} onClose={() => setAppointmentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Schedule Follow-up Appointment
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Based on your {selectedResult?.riskLevel} risk assessment results, we recommend scheduling a follow-up appointment.
            </Alert>
            
            <Typography variant="body1" gutterBottom>
              <strong>Assessment:</strong> {selectedResult?.title}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <strong>Risk Level:</strong> {selectedResult?.riskLevel}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <strong>Your counselor will contact you within:</strong>
            </Typography>
            <Typography variant="body2" color="primary" sx={{ ml: 2, mb: 2 }}>
              {selectedResult?.riskLevel === 'High' ? '24 hours' : selectedResult?.riskLevel === 'Moderate' ? '48 hours' : '1 week'}
            </Typography>
            
            <Typography variant="body2" color="textSecondary">
              You can also call our counseling center directly at (555) 123-4567 if you need immediate assistance.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAppointmentDialog(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAppointmentSubmit}>
            Request Appointment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ResultsPage;